import axios from "axios";
import { enqueueSnackbar } from "notistack";
import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import Divider from "@mui/material/Divider";

import { APIEndpoints } from "@/apis/endpoints";
import { useUserContext } from "@/utils/contexts/UserContext";
import { extractErrorMessage, useBackend } from "@/utils/misc";

export default function RestartDropOffConfirmationDialog({
  confirmationDialogOpen,
  setConfirmationDialogOpen,
  target,
}) {
  const user = useUserContext();
  const base_url = `${APIEndpoints.LEARNING}/company/${
    user.current_company.id
  }`;
  const { t } = useTranslation();
  const url = `${base_url}/total_reset`;
  const { data } = useBackend(url);
  const [sendInProgress, setSendInProgress] = useState(false);
  let total = 0;
  if (data) {
    total =
      target === "drop_off" ? data.total_drop_off : data.total_not_started;
  }

  const title =
    target === "drop_off"
      ? t("Restart courses of drop off users")
      : t("Remind not started users");
  const onClickReset = async (e) => {
    setSendInProgress(true);
    try {
      await axios
        .post(`${base_url}/schedule_reset`, { target: target })
        .then(() => {
          enqueueSnackbar("Email was sent successfully ", {
            variant: "success",
            autoHideDuration: 3000,
          });
          setSendInProgress(false);
          setConfirmationDialogOpen(false);
        });
    } catch (error) {
      setSendInProgress(false);
      enqueueSnackbar(extractErrorMessage(error), { variant: "error" });
    }
  };

  const onClickSendPreviewEmail = async (e) => {
    setSendInProgress(true);
    try {
      await axios
        .post(`${APIEndpoints.LEARNING}/user/preview_email_reset`, {
          target: target,
        })
        .then(() => {
          enqueueSnackbar("Email was sent successfully ", {
            variant: "success",
          });
          setSendInProgress(false);
        });
    } catch (error) {
      setSendInProgress(false);
      enqueueSnackbar(extractErrorMessage(error), { variant: "error" });
    }
  };

  return (
    <Dialog open={confirmationDialogOpen} maxWidth={"xs"} fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <Divider />
      {data && (
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography sx={{ textAlign: "center" }}>
              <Trans
                i18nKey="RestartDropOffConfirmationDialogText"
                values={{
                  total: total,
                }}
              >
                <strong>
                  By clicking Send now, the {total} user(s) who have been
                  inactive for more than 30 days will lose their progress on
                  their ongoing tutorial. They will receive a notification email
                  to inform them and a new invitation on Slack/Teams/Webchat to
                  complete a course.
                </strong>
              </Trans>
            </Typography>
          </Box>
        </DialogContent>
      )}

      <Divider />

      <DialogActions>
        <Box display="flex" justifyContent="space-between" width="100%">
          <Button
            disabled={sendInProgress}
            onClick={() => setConfirmationDialogOpen(false)}
          >
            {t("Cancel")}
          </Button>
          <Box>
            <Button
              disabled={sendInProgress}
              onClick={onClickSendPreviewEmail}
              variant={"outlined"}
              sx={{ marginRight: "5px" }}
            >
              {t("Send me an example")}
            </Button>
            <Button
              disabled={sendInProgress}
              disableElevation
              onClick={onClickReset}
              variant={"contained"}
            >
              {t("Send now")}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
